/* istanbul ignore file */
import { useEffect, useState } from 'react';
import { useShowContext } from 'framework/show';
import useLeadTemplatedEnvelopeService, {
  SEND_TEMPLATED_ENVELOPE,
  TEMPLATE_TYPES,
} from 'domain/sales/lead/docusign/templatedEnvelopeLeadService';
import { ButtonWithConfirmation } from 'framework/ui/ui-builder';
import { useMessageService, SuccessMessage } from 'framework/ui/Message';
import { useAsyncValue } from 'framework/utils';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { Typography } from '@mui/material';

const SendLeadTemplatedEnvelopeButton = ({ permissionToCheck }: { permissionToCheck?: string }) => {
  const { getUserName, getWorkingEmail } = useAuthenticatedUserService();
  const { sendTemplatedEnvelope } = useLeadTemplatedEnvelopeService();
  const [isVisible, setIsVisible] = useState(true);
  const { addMessage } = useMessageService();
  const { data } = useShowContext();
  const clientName = `${data.getValueBySource('firstName')} ${data.getValueBySource('lastName')}`;
  const clientEmail = `${data.getValueBySource('email')}`;
  const currentUserName = useAsyncValue(async () => getUserName());
  const currentUserEmail = useAsyncValue(async () => getWorkingEmail());
  const currentUserFullName = `${currentUserName?.firstName} ${currentUserName?.lastName}`;

  useEffect(() => {
    if (currentUserName == null || currentUserEmail == null) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
  }, [currentUserName, currentUserEmail]);

  const initialFormData = {
    templateType: TEMPLATE_TYPES.LEAD_NDA,
    customerSigner: {
      name: clientName,
      email: clientEmail,
    },
    companySigner: {
      name: currentUserFullName,
      email: currentUserEmail,
    },
  };

  return isVisible ? (
    <ButtonWithConfirmation
      displayMessage="Please review carefully!"
      submitMethod={() => sendTemplatedEnvelope(initialFormData)}
      variant="outlined"
      permissionToCheck={SEND_TEMPLATED_ENVELOPE}
      modalChildren={
        <>
          <Typography variant="body1" mb={2}>
            Once you click <b>Send</b> button, a Mutual Non-Disclosure Agreement (NDA) will be sent to:{' '}
            <b>
              {clientName} &lt;{clientEmail}&gt;
            </b>
          </Typography>
          <Typography variant="body1" mb={2}>
            You ({currentUserFullName}) are granted an authorization by Syberry Corporation to sign the NDA, and will
            receive a request from DocuSign to do so. If you prefer another person to sign the NDA, ask them to send an
            NDA signing request instead.
          </Typography>
          <Typography variant="body1" component="p" mb={0}>
            If any of the information above is incorrect, then please close this popup and update the details in the
            Lead form before sending.
          </Typography>
        </>
      }
      modalConfirmButtonText="Send"
      afterSubmit={{
        execute: () => {
          addMessage(
            new SuccessMessage(
              'You are awesome! NDA has been sent. Information about the status of the document will appear within a few minutes.',
            ),
          );
          setIsVisible(false);
        },
      }}
      closeOnSubmit={false}
    >
      Send NDA
    </ButtonWithConfirmation>
  ) : null;
};

export default SendLeadTemplatedEnvelopeButton;
